/**
 * Access → Policies
 *
 * Authored rules on the left of the workflow, the compiled + Ed25519-signed
 * bundle on the right. "Compile & Sign" is the step that makes edits reach the
 * fleet: rules are inert until they are compiled into a signed bundle that
 * nodes pull.
 *
 * PHASE-1 SCOPE. Deferred from this page: rule editing (create/delete only for
 * now), the WHEN dimension (time window + geo, which the API already stores and
 * compiles), and version-to-version diffing.
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Trash2, Pencil, ShieldCheck, FileSignature, Hash } from 'lucide-react';
import DataTable, { type Column } from '../../components/common/DataTable';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import StatusBadge from '../../components/common/StatusBadge';
import StatCard from '../../components/common/StatCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import PolicyRuleModal from '../../components/access/PolicyRuleModal';
import type { AccessApp, AppGroup, IdentityGroup, PostureProfile } from '../../services/access.service';
import vpnUserService, { type VPNUser } from '../../services/vpnUser.service';
import ZtnaChip from '../../components/access/ZtnaChip';
import { useToast } from '../../components/common/ToastContainer';
import accessService, {
  type AccessPolicyRule,
  type AccessPolicyVersion,
  type CreatePolicyRuleData,
} from '../../services/access.service';
import { fmtRequirement } from '../../data/postureCatalog';

function listOrDash(values: string[] | null | undefined): string {
  return values && values.length > 0 ? values.join(', ') : '—';
}

const AccessPolicies = () => {
  const toast = useToast();

  const [rules, setRules] = useState<AccessPolicyRule[]>([]);
  const [active, setActive] = useState<AccessPolicyVersion | null>(null);
  const [versions, setVersions] = useState<AccessPolicyVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [compiling, setCompiling] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AccessPolicyRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AccessPolicyRule | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Directory sources for the rule modal's "Who" pickers (VSNP-1162): the
  // tenant's identity groups and its VPN users. Non-fatal on failure — the
  // pickers simply fall back to free-text entry.
  const [identityGroups, setIdentityGroups] = useState<IdentityGroup[]>([]);
  const [vpnUsers, setVpnUsers] = useState<VPNUser[]>([]);
  const [apps, setApps] = useState<AccessApp[]>([]);
  const [appGroups, setAppGroups] = useState<AppGroup[]>([]);
  const [postureProfiles, setPostureProfiles] = useState<PostureProfile[]>([]);

  const fetchDirectory = useCallback(async () => {
    // Each picker source is loaded INDEPENDENTLY: one failing call must not
    // blank the others. (A single Promise.all previously rejected on any one
    // error — e.g. an out-of-range limit — and cleared every dropdown.)
    const settled = await Promise.allSettled([
      accessService.listIdentityGroups(),
      vpnUserService.list({ pageSize: 200 }),
      accessService.listApps({ limit: 200 }),
      accessService.listAppGroups(),
      accessService.listPostureProfiles(),
    ]);
    const [groups, users, appList, appGroupList, profileList] = settled;
    setIdentityGroups(groups.status === 'fulfilled' ? groups.value ?? [] : []);
    setVpnUsers(users.status === 'fulfilled' ? users.value?.items ?? [] : []);
    setApps(appList.status === 'fulfilled' ? appList.value?.items ?? [] : []);
    setAppGroups(appGroupList.status === 'fulfilled' ? appGroupList.value ?? [] : []);
    setPostureProfiles(profileList.status === 'fulfilled' ? profileList.value ?? [] : []);
  }, []);

  useEffect(() => {
    void fetchDirectory();
  }, [fetchDirectory]);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [ruleList, activeBundle, versionList] = await Promise.all([
        accessService.listPolicyRules(),
        accessService.getActivePolicy(),
        accessService.listPolicyVersions(10),
      ]);
      setRules(ruleList ?? []);
      setActive(activeBundle ?? null);
      setVersions(versionList ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load policies');
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const handleCreate = async (data: CreatePolicyRuleData) => {
    try {
      await accessService.createPolicyRule(data);
      toast.success('Policy created', `${data.name} — compile to push it to the fleet`);
      setIsAddOpen(false);
      await fetchAll();
    } catch (err) {
      toast.error('Create failed', err instanceof Error ? err.message : 'Could not create the policy');
      throw err;
    }
  };

  const handleUpdate = async (data: CreatePolicyRuleData) => {
    if (!editTarget) return;
    try {
      await accessService.updatePolicyRule(editTarget.id, data);
      toast.success('Policy updated', `${data.name} — compile to push the change to the fleet`);
      setEditTarget(null);
      await fetchAll();
    } catch (err) {
      toast.error('Update failed', err instanceof Error ? err.message : 'Could not update the policy');
      throw err;
    }
  };

  // Inline enable/disable, no modal. A disabled policy is authored but not
  // compiled into the bundle — recompile to push the change to the fleet.
  const handleToggleEnabled = async (rule: AccessPolicyRule) => {
    setTogglingId(rule.id);
    try {
      await accessService.updatePolicyRule(rule.id, { enabled: rule.enabled !== 1 });
      toast.success(rule.enabled ? 'Policy disabled' : 'Policy enabled', `${rule.name} — recompile to apply`);
      await fetchAll();
    } catch (err) {
      toast.error('Update failed', err instanceof Error ? err.message : 'Could not update the policy');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accessService.deletePolicyRule(deleteTarget.id);
      toast.success('Policy deleted', deleteTarget.name);
      setDeleteTarget(null);
      await fetchAll();
    } catch (err) {
      toast.error('Delete failed', err instanceof Error ? err.message : 'Could not delete the policy');
    } finally {
      setDeleting(false);
    }
  };

  const handleCompile = async () => {
    try {
      setCompiling(true);
      const result = await accessService.compilePolicy();
      if (result.unchanged) {
        toast.info('No changes', `Bundle ${result.version} is already current`);
      } else {
        toast.success('Bundle signed', `Version ${result.version} — ${result.ruleCount} ${result.ruleCount === 1 ? 'policy' : 'policies'}`);
      }
      await fetchAll();
    } catch (err) {
      toast.error('Compile failed', err instanceof Error ? err.message : 'Could not compile the policy bundle');
    } finally {
      setCompiling(false);
    }
  };

  // Resolve rule id references to display names for the Device and What columns.
  const appNameById = new Map(apps.map((a) => [a.id, a.display_name || a.name]));
  const appGroupNameById = new Map(appGroups.map((g) => [g.id, g.name]));
  const profileById = new Map(postureProfiles.map((p) => [p.id, p]));

  const ruleColumns: Column<AccessPolicyRule>[] = [
    { key: 'priority', label: 'Priority', sortable: true },
    {
      key: 'name',
      label: 'Policy',
      sortable: true,
      render: (rule) => (
        <div>
          <div className="font-medium text-gray-900">{rule.name}</div>
          {rule.description && <div className="text-xs text-gray-500">{rule.description}</div>}
        </div>
      ),
    },
    { key: 'who', label: 'Who', render: (rule) => listOrDash([...(rule.subject_groups ?? []), ...(rule.subject_users ?? [])]) },
    {
      key: 'device',
      label: 'Device',
      render: (rule) => {
        // The Device column shows the Posture Profile NAME (requirements on
        // hover), never the predicates. A pre-profile policy with inline
        // requirements shows a "legacy" chip. "Any" only when there is
        // genuinely no device constraint.
        const profile = rule.posture_profile_id ? profileById.get(rule.posture_profile_id) : undefined;
        const legacy = !rule.posture_profile_id && rule.device_posture ? Object.entries(rule.device_posture) : [];
        const managed = rule.require_managed_device === 1;
        const hasPosture = !!rule.posture_profile_id || legacy.length > 0;
        return (
          <div className="text-sm">
            {managed && <div className="text-gray-700">Managed only</div>}
            {!managed && !hasPosture && <div className="text-gray-400">Any</div>}
            {rule.posture_profile_id && (
              <span
                className="inline-flex items-center rounded bg-teal-50 border border-teal-200 px-1.5 py-0.5 text-[11px] text-teal-700"
                title={
                  profile
                    ? Object.entries(profile.requirements ?? {}).map(([k, v]) => fmtRequirement(k, v)).join('\n')
                    : 'Posture profile'
                }
              >
                ⛨ {profile?.name ?? rule.posture_profile_id}
              </span>
            )}
            {legacy.length > 0 && (
              <span
                className="inline-flex items-center rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[11px] text-amber-700"
                title={`Inline requirements (pre-profile):\n${legacy.map(([k, v]) => fmtRequirement(k, v)).join('\n')}`}
              >
                Inline (legacy)
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'what',
      label: 'What',
      render: (rule) => {
        const appNames = (rule.app_ids ?? []).map((id) => appNameById.get(id) ?? id);
        const groupNames = (rule.app_group_ids ?? []).map((id) => appGroupNameById.get(id) ?? id);
        const tags = rule.app_tags ?? [];
        if (appNames.length === 0 && groupNames.length === 0 && tags.length === 0) {
          return <span className="text-gray-400" title="Matches any application">any app</span>;
        }
        return (
          <div className="flex flex-wrap gap-1 text-xs">
            {appNames.map((n) => (
              <span key={`a-${n}`} className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">
                {n}
              </span>
            ))}
            {groupNames.map((n) => (
              <span key={`g-${n}`} className="inline-flex items-center rounded border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-indigo-700" title="Application group">
                ◫ {n}
              </span>
            ))}
            {tags.map((t) => (
              <span key={`t-${t}`} className="inline-flex items-center rounded border border-gray-200 px-1.5 py-0.5 text-gray-500" title="App tag">
                #{t}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'action',
      label: 'Action',
      sortable: true,
      render: (rule) => (
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded border ${
            rule.action === 'allow'
              ? 'bg-success-100 text-success-700 border-success-200'
              : 'bg-danger-100 text-danger-700 border-danger-200'
          }`}
          title={rule.action === 'allow' ? 'Grants access when matched' : 'Blocks access when matched (place above the grant it carves out of)'}
        >
          {rule.action === 'allow' ? 'Grant' : 'Block'}
        </span>
      ),
    },
    {
      key: 'enabled',
      label: 'Enabled',
      render: (rule) => (
        <ToggleSwitch
          checked={rule.enabled === 1}
          busy={togglingId === rule.id}
          onChange={() => handleToggleEnabled(rule)}
          label={rule.enabled ? `Disable ${rule.name}` : `Enable ${rule.name}`}
          title={rule.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
        />
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (rule) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-primary-600"
            title="Edit policy"
            onClick={() => setEditTarget(rule)}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-danger-600"
            title="Delete policy"
            onClick={() => setDeleteTarget(rule)}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Access Policies</h1>
            <ZtnaChip />
          </div>
          <p className="text-gray-600 mt-1">
            Policies are evaluated by ascending priority, first match wins. The tenant default is deny.
          </p>
        </div>
        <div className="flex space-x-2">
          <button type="button" className="btn-secondary" onClick={() => void fetchAll()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button type="button" className="btn-secondary" onClick={() => void handleCompile()} disabled={compiling}>
            <FileSignature className="w-4 h-4 mr-2 inline" />
            {compiling ? 'Compiling…' : 'Compile & Sign'}
          </button>
          <button type="button" className="btn-primary" onClick={() => setIsAddOpen(true)}>
            <Plus className="w-4 h-4 mr-2 inline" />
            Add Policy
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <StatCard title="Policies" value={rules.length} icon={ShieldCheck} color="primary" loading={loading} />
        <StatCard
          title="Active Bundle"
          value={active?.version ?? 'not compiled'}
          icon={FileSignature}
          color={active ? 'success' : 'warning'}
          subtitle={active ? `epoch ${active.epoch} · ${active.ruleCount} ${active.ruleCount === 1 ? 'policy' : 'policies'}` : 'Compile to publish'}
          loading={loading}
        />
        <StatCard
          title="Bundle Digest"
          value={active ? `${active.sha256.slice(0, 12)}…` : '—'}
          icon={Hash}
          color="info"
          subtitle={active ? `signed with ${active.keyId}` : undefined}
          loading={loading}
        />
      </div>

      <div className="card">
        <DataTable
          columns={ruleColumns}
          data={rules}
          loading={loading}
          searchPlaceholder="Search policies…"
          emptyMessage="No policies yet — everything is denied by default"
        />
      </div>

      {versions.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Bundle History</h2>
          <ul className="divide-y divide-gray-100">
            {versions.map((v) => (
              <li key={v.id} className="py-2 flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium text-gray-900">{v.version}</span>
                  <span className="text-gray-500 ml-2">
                    epoch {v.epoch} · {v.ruleCount} {v.ruleCount === 1 ? 'policy' : 'policies'} · {v.createdBy ?? 'system'}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <code className="text-xs text-gray-500">{v.sha256.slice(0, 16)}…</code>
                  <StatusBadge status={v.status === 'active' ? 'active' : 'inactive'} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* One modal instance for both create and edit; keyed so it remounts (and
          re-seeds from the target rule) each time it opens. */}
      {(isAddOpen || editTarget) && (
        <PolicyRuleModal
          key={editTarget?.id ?? 'new'}
          isOpen
          rule={editTarget}
          onClose={() => (editTarget ? setEditTarget(null) : setIsAddOpen(false))}
          onSave={editTarget ? handleUpdate : handleCreate}
          identityGroups={identityGroups}
          vpnUsers={vpnUsers}
          apps={apps}
          appGroups={appGroups}
          postureProfiles={postureProfiles}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete policy"
        message={`Delete '${deleteTarget?.name}'? Recompile the bundle afterwards to push the change to the fleet.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default AccessPolicies;
